import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useStoresUI } from '@context/StoresContext';
import { usePdp } from '@context/PdpContext';
import useDeliveryStockLevel from '@utils/delivery/useDeliveryStockLevel';
import SelectVariantMessage from './SelectVariantMessage';
import SelectStoreMessage from './SelectStoreMessage';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';

import { StorefrontIcon, TruckIcon } from '@phosphor-icons/react';
import StoreAvailabilityMessage from './StoreAvailabilityMessage';
import FulfillmentStockLevelRow from './FulfillmentStockLevelRow';

import { getPDPShippingOptions } from '@utils/shipping/getPDPShippingOptions';
import { ShippingRestrictions } from '@adapters/bigcommerce/types/product';
import { useLocale } from '@hooks/useLocale';
import { canShipToAddress } from '@utils/fulfillment';

import styles from './ProductFulfillment.module.scss';

const ProductFulfillment = () => {
  const { t } = useTranslation('pdp');
  const locale = useLocale();
  const {
    selectedVariant,
    colours,
    sizes,
    shippingRestrictions,
    selectedAvailability,
  } = usePdp();

  const { favouriteStoreDetails } = useStoresUI();
  const isClient = useHasMounted();
  const stockLevelResult = useDeliveryStockLevel();
  const { stockLevel, stockLevelMessage } = stockLevelResult;
  const shippingOption =
    getPDPShippingOptions(
      shippingRestrictions as ShippingRestrictions,
      locale ?? 'en'
    ) ?? null;

  // A pickup-only variant keeps the ship box, greyed out as it is before a
  // variant is chosen. Unrestricted variants are unaffected.
  const canShip = canShipToAddress(
    selectedVariant?.fulfillmentRestriction,
    shippingRestrictions as ShippingRestrictions
  );
  const isShipDisabled = !selectedVariant || !canShip;

  // Pickup looks disabled until a store is chosen, unless it's the only option:
  // then grey makes the item look unavailable.
  const isDisabled =
    !selectedVariant ||
    (selectedAvailability && canShip && !favouriteStoreDetails);

  return (
    <div className={styles.fulfillmentMethodsContainer}>
      {selectedAvailability && (
        <div className={styles.fulfillmentMethods}>
          <div className={styles.fulfillmentOptionContent}>
            <div className={styles.fulfillmentOptionHeader}>
              <TruckIcon
                width={20}
                height={20}
                className={cn(styles.fulfillmentOptionHeaderIcon, {
                  [styles.fullfillmentOptionDisabled]: isShipDisabled,
                })}
              />
              <span
                className={cn(styles.fulfillmentOptionTitle, {
                  [styles.fullfillmentOptionDisabled]: isShipDisabled,
                })}
              >
                {t('product.shipping.message.new')}
              </span>
            </div>
            <div
              className={cn(
                styles.fulfillmentOptionFlexContainer,
                styles.shippingFulfillmentOptionFlexContainer
              )}
            >
              {!selectedVariant && (
                <SelectVariantMessage
                  colours={colours?.length}
                  sizes={sizes?.length}
                  isAddress
                />
              )}
              {selectedVariant && !canShip ? (
                <span
                  className={cn(
                    styles.fulfillmentOptionDescription,
                    styles.fulfillmentOptionDescriptionDisabled
                  )}
                >
                  {t('product.shipping.message.delivery.unavailable')}
                </span>
              ) : (
                <>
                  <span className={styles.fulfillmentOptionDescription}>
                    {selectedVariant &&
                      t('product.shipping.message.delivery.time', {
                        deliveryTime: shippingOption?.estimatedDeliveryDate,
                      })}
                  </span>

                  <div className={styles.stockLevelRowContainer}>
                    <FulfillmentStockLevelRow
                      stockLevel={stockLevel}
                      stockLevelMessage={stockLevelMessage}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <div className={styles.fulfillmentMethods}>
        <div className={styles.fulfillmentOptionContent}>
          {!selectedAvailability && (
            <span
              className={cn(styles.inStoreOnlyMessage, {
                [styles.inStoreOnlyMessageDisabled]: isDisabled,
              })}
            >
              {t('product.free.store.pickup.new.inStoreOnly')}
            </span>
          )}
          <div className={styles.fulfillmentOptionHeader}>
            <StorefrontIcon
              width={20}
              height={20}
              className={cn(styles.fulfillmentOptionHeaderIcon, {
                [styles.fullfillmentOptionDisabled]: isDisabled,
              })}
            />
            <span
              className={cn(styles.fulfillmentOptionTitle, {
                [styles.fullfillmentOptionDisabled]: isDisabled,
              })}
            >
              {t('product.free.store.pickup.new')}
            </span>
          </div>
          <div className={styles.fulfillmentOptionFlexContainer}>
            {isClient && !favouriteStoreDetails?.data?.mec_store_id && (
              <SelectStoreMessage isDisabled={isDisabled} />
            )}

            {isClient &&
              favouriteStoreDetails?.data?.mec_store_id &&
              !selectedVariant && (
                <SelectVariantMessage
                  colours={colours?.length}
                  sizes={sizes?.length}
                />
              )}

            {favouriteStoreDetails?.data?.mec_store_id && (
              <StoreAvailabilityMessage
                storeId={favouriteStoreDetails?.data?.mec_store_id}
                selectedAvailability={selectedAvailability}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFulfillment;
